import React, { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import { API_BASE_URL } from "../../../../config";

const fieldMap = {
  "Lead Date": "leadDate",
  "Lead From": "leadFrom",
  "Customer Name": "customerName",
  "Customer Number": "customerNumber",
  "Customer Address": "customerAddress",
  "Required Flat Type": "requiredFlatType",
  "Location Required": "locationRequired",
  "Required Property Type": "requiredResaleOrNewFlat",
  "Budget": "budget",
  "Customer Feedback Category": "customerFeedbackCategory",
  "Follow-up Date": "followupDate",
  "Call Type": "callType",
  "Parking": "parking",
  "Client Feedback": "clientFeedback",
  "Added By": "addedBy",
};


function normalizeInitialData(data) {
  const normalized = {};
  Object.values(fieldMap).forEach((key) => {
    if (key === "addedBy") {
      normalized[key] =
        data.addedBy && typeof data.addedBy === "object"
          ? data.addedBy._id || ""
          : typeof data.addedBy === "string"
            ? data.addedBy
            : "";
    } else if (["leadDate", "followupDate"].includes(key)) {

      if (data[key]) {
        // Convert UTC → Local
        const date = new Date(data[key]);
        const localValue = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16); // "YYYY-MM-DDTHH:mm"
        normalized[key] = localValue;
      } else {
        // Default: current local time
        normalized[key] = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16);
      }

    } else {
      normalized[key] = data[key] !== undefined ? data[key] : "";
    }
  });

  // ✅ Add extra fields
  // normalized.callBackDateTime = data.callBackDateTime
  //   ? data.callBackDateTime.slice(0, 16)
  //   : "";
  // normalized.visitScheduleDateTime = data.visitScheduleDateTime
  //   ? data.visitScheduleDateTime.slice(0, 16)
  //   : "";

  normalized.callBackDateTime = data.callBackDateTime
    ? new Date(new Date(data.callBackDateTime).getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)
    : "";

  // ✅ Convert UTC → Local for visitScheduleDateTime
  normalized.visitScheduleDateTime = data.visitScheduleDateTime
    ? new Date(new Date(data.visitScheduleDateTime).getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)
    : "";
  normalized.otherFeedbackText = data.otherFeedbackText || "";

  return normalized;
}

const EditCustomerForm = ({ onClose, initialData = {}, onSuccess }) => {
  const [formData, setFormData] = useState(normalizeInitialData(initialData));
  const [admins, setAdmins] = useState([]);


  const [errors, setErrors] = useState({
    customerNumber: "",
    customerAddress: "",
    budget: "",
  });

  useEffect(() => {
    setFormData(normalizeInitialData(initialData));
  }, [initialData]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/get-all-subadmin`,
          { headers: { token } }
        );
        if (res.data.success) {
          setAdmins(res.data.result.subAdmins || []);
        }
      } catch (err) {
        // console.error("Failed to load admins", err);
      }
    };
    fetchAdmins();
  }, []);

  // ✅ Format budget as currency
  const formatBudget = (value) => {
    if (!value) return "";
    const num = value.replace(/\D/g, ""); // remove non-digits
    if (!num) return "";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = type === "checkbox" ? e.target.checked : value;

    // ✅ Customer Number validation
    if (name === "customerNumber") {
      if (/^\d*$/.test(newValue)) {
        setErrors((prev) => ({ ...prev, customerNumber: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          customerNumber: "Customer number must contain digits only.",
        }));
      }
    }

    // ✅ Customer Address validation
    if (name === "customerAddress") {
      const regex = /^[a-zA-Z0-9\s,.\-/#()]+$/;
      if (newValue && !regex.test(newValue)) {
        setErrors((prev) => ({
          ...prev,
          customerAddress:
            "Invalid address. Only letters, numbers, spaces & ,.-/#() allowed.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, customerAddress: "" }));
      }
    }

    // ✅ Budget formatting
    if (name === "budget") {
      const digitsOnly = newValue.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
      setErrors((prev) => ({ ...prev, budget: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  function toLocalISOString(dateStr) {
    if (!dateStr) return null;
    // datetime-local input gives local time → convert to UTC for backend
    return new Date(dateStr).toISOString();
  }

  const handleUpdate = async () => {
    // ✅ Prevent submission if validation errors
    if (errors.customerNumber || errors.customerAddress) {
      toast.error("Please fix validation errors before saving.");
      return;
    }

    const toastId = toast.loading("Updating customer...");
    try {
      const token = localStorage.getItem("token");

      const leadDateIST = toLocalISOString(formData.leadDate);
      const followupDateIST = toLocalISOString(formData.followupDate);

      const payload = {
        ...formData, leadDate: leadDateIST,
        followupDate: followupDateIST
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/customer/edit-customer/${initialData._id}`,
        payload,
        { headers: { token } }
      );
      toast.success(response.data.message || "Customer updated successfully", {
        id: toastId,
      });
      if (onSuccess) onSuccess(response.data.result);
      setFormData(normalizeInitialData(response.data.result));
      setTimeout(onClose, 1200);
    } catch (error) {
      const message =
        error.response?.data?.message || "Error while updating customer";
      toast.error(message, { id: toastId });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center overflow-y-auto p-4 backdrop-blur-sm">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="mt-10 mb-10 w-full max-w-full sm:max-w-5xl bg-white dark:bg-navy-800 rounded-lg shadow-xl p-4 sm:p-6 overflow-y-auto max-h-[calc(100vh-40px)]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">
            Edit Customer
          </h2>
          <button
            className="rounded bg-red-700 px-2 py-2 text-white transition hover:bg-red-800"
            onClick={onClose}
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Form */}
        <form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Object.keys(fieldMap).map((label, idx) => {
            const key = fieldMap[label];
            const isDateField = key === "leadDate" || key === "followupDate";

            return (
              <div key={idx} className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                  {label}
                </label>

                {key === "requiredResaleOrNewFlat" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Resale">Resale</option>
                    <option value="New">New</option>
                    <option value="Both">Both</option>
                  </select>
                ) : key === "callType" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Fresh Call">Fresh Call</option>
                    <option value="Re-call">Re-call</option>
                  </select>
                ) : key === "parking" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : key === "customerFeedbackCategory" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Ringing/Busy">Ringing/Busy</option>
                    <option value="Call back">Call back</option>
                    <option value="Need With Cost Sheet Share">Need With Cost Sheet Share</option>
                    <option value="Cost Sheet Shared">Cost Sheet Shared</option>
                    <option value="Visit schedule">Visit schedule</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Booking Done">Booking Done</option>
                    <option value="Visit Done">Visit Done</option>
                    <option value="Other">Other</option>
                  </select>
                ) : key === "addedBy" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    disabled
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Admin</option>
                    {admins.map((admin) => (
                      <option key={admin._id} value={admin._id}>
                        {admin.adminName}
                      </option>
                    ))}
                  </select>
                ) : key === "budget" ? (
                  <>
                    <input
                      type="text"
                      name={key}
                      value={formatBudget(formData[key])}
                      onChange={handleChange}
                      placeholder="Enter Budget"
                      className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                    />
                    {errors.budget && (
                      <span className="text-xs text-red-500">
                        {errors.budget}
                      </span>
                    )}
                  </>
                ) : (
                  <input
                    type={isDateField ? "datetime-local" : "text"}
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  />
                )}

                {/* ✅ Inline error messages */}
                {key === "customerNumber" && errors.customerNumber && (
                  <span className="text-xs text-red-500">
                    {errors.customerNumber}
                  </span>
                )}
                {key === "customerAddress" && errors.customerAddress && (
                  <span className="text-xs text-red-500">
                    {errors.customerAddress}
                  </span>
                )}
              </div>
            );
          })}

          {/* ✅ Conditional Extra Fields */}
          {formData.customerFeedbackCategory === "Call back" && (
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Call Back Date & Time
              </label>
              <input
                type="datetime-local"
                name="callBackDateTime"
                value={formData.callBackDateTime}
                onChange={handleChange}
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Visit schedule" && (
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Visit Schedule Date & Time
              </label>
              <input
                type="datetime-local"
                name="visitScheduleDateTime"
                value={formData.visitScheduleDateTime}
                onChange={handleChange}
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Other" && (
            <div className="flex flex-col col-span-full">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Other Feedback
              </label>
              <input
                type="text"
                name="otherFeedbackText"
                value={formData.otherFeedbackText}
                onChange={handleChange}
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Need With Cost Sheet Share" && (
            <div className="col-span-full flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Notes*
              </label>
              <input
                type="text"
                name="otherFeedbackText"
                value={formData.otherFeedbackText}
                onChange={handleChange}
                placeholder="Enter notes"
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

        </form>

        {/* Submit button */}
        <div className="col-span-full mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-5 py-2 rounded-md text-sm sm:text-base transition"
          >
            Update Customer
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCustomerForm;


